/**
 * API calls using the API client
 */
import axios from 'axios'
import debug from 'debug'
import Session from './session'
import client from './client'

const dbg = debug('api:web:')

const { url, baseURL, baseURLPath, isReady, version, GET, DELETE, POST, PUT, PATCH } = client

export default {
  url,
  baseURL,
  baseURLPath,
  isReady,
  version,

  /**
   * Login using username and password
   */
  login (user, pwd) {
    const data = { grant_type: 'password', username: user, password: pwd }
    return this.auth(data)
  },

  /**
   * Login with oauth2 token
   */
  tokenLogin (clientId, code, redirectUri) {
    const data = {
      grant_type: 'authorization_code',
      client_id: clientId,
      redirect_uri: redirectUri,
      code: code
    }
    Session.store({ client_id: clientId })
    return this.auth(data)
  },

  embeddedLogin (data) {
    return new Promise((resolve, reject) => {
      axios({
        method: 'post',
        url: this.url('provisionings/clearly_anywhere/webview_auth'),
        data: data
      }).then(resp => {
        dbg('Successfuly logged in with clearly anywhere embedded mode')
        Session.store(resp.data)
        resolve()
      }).catch(err => {
        dbg('Failed to log in with clearly anywhere embedded mode')
        dbg(err.response)
        Session.clear()
        const error = err.response && err.response.data.error_description
          ? err.response.data.error_description
          : 'login.error.login'
        reject(new Error(error))
      })
    })
  },

  /**
   * API auth request
   */
  auth (data) {
    const id = data.username ? data.username : data.client_id
    return new Promise((resolve, reject) => {
      axios({
        method: 'post',
        url: this.url('oauth/token', false),
        data: data
      }).then(resp => {
        dbg(`Successfuly loged in. Grant type: ${data.grant_type}. Id: ${id}`)
        Session.store(resp.data)
        return GET(this.url('users/validate_callpanel_session'))
      }).then(resp => { // check if valid session user
        if (resp?.data?.success) {
          dbg('Valid callpanel session')
          resolve()
        } else {
          dbg('Invalid callpanel session')
          Session.clear()
          reject(new Error('login.error.session'))
        }
      }).catch(err => {
        dbg(`Failed loged in. Grant type: ${data.grant_type}. Id: ${id}`)
        dbg(err.response)
        Session.clear()
        const error = err.response && err.response.data.error_description
          ? err.response.data.error_description
          : 'login.error.login'
        reject(new Error(error))
      })
    })
  },

  /**
   * Get client id and redirect url for oauth2 login
   */
  oauthAppCallpanel () {
    return axios.get(this.url('oauth_applications/callpanel'))
  },

  /**
   * get exception from response.
   * returns false if not API exception
   */
  getException (err) {
    if (err?.response?.data?.exception) {
      return err.response.data.exception
    }
    return false
  },

  /**
   * Get permissions list.
   */
  permissions () {
    return GET(this.url('users/permissions'))
  },

  /**
   * Get SIP UA configuration.
   * returns Promise
   */
  cfgSIPUA () {
    return GET(this.url('config/sipua'))
  },

  /**
   * Get RabbitMQ configuration.
   * returns Promise
   */
  cfgRMQ () {
    return GET(this.url('config/rmqclient'))
  },

  /**
   * Get Parkings.
   * returns Promise
   */
  getParkings () {
    return GET(this.url('parkings'))
  },

  /**
   * Get panel users
   * @param {Number} pagination start from
   * @param {Number} pagination limit
   * @param {String} search filter
   * returns Promise
   */
  getPanelUsers (start, limit, search) {
    const config = {
      params: {
        start: start,
        limit: limit
      }
    }
    if (search) {
      config.params.search = search
    }
    return GET(this.url('panel_buddies'), config)
  },

  /**
   * Get panel buddies' cards
   *
   * returns Promise
   */
  getPanelBuddies () {
    return GET(this.url('panel_buddies/my_buddies'))
  },

  /**
   * Returns list of queues in Promise
   */
  getQueues (start = 0, limit = 10) {
    const config = {
      params: { start: start, limit: limit }
    }
    return GET(this.url('queues'), config)
  },

  /**
   * Returns a list of all the queue names
   */
  getAllQueueNames () {
    return GET(this.url('queues/all_queue_names'))
  },

  /**
   * Returns list all watched queues in Promise
   */
  getAllWatchedQueues () {
    return GET(this.url('queues/list_watched'))
  },

  /**
   * Returns list of conferences in Promise
   */
  getConferences (start = 0, limit = 8) {
    const config = {
      params: { start: start, limit: limit }
    }
    return GET(this.url('conferences'), config)
  },

  /**
   * Returns list of video conferences in Promise
   */
  getVideoConferences (start = 0, limit = 10, excludeDynamic = true) {
    const config = {
      params: { start: start, limit: limit, exclude_dynamic: excludeDynamic }
    }
    return GET(this.url('video_conferences'), config)
  },

  /**
   * Create a video conference
   */
  createVideoConference (data) {
    return POST(this.url('video_conferences'), data)
  },

  /**
   * Update a video conference
   */
  updateVideoConference (data) {
    return PUT(this.url(`video_conferences/${data.id}`), data)
  },

  /**
   * Delete a video conference
   */
  deleteVideoConference (data) {
    return DELETE(this.url(`video_conferences/${data.id}`))
  },

  /**
   * Returns list of personal conferences
   */
  getPersonalConference () {
    return GET(this.url('personal_conferences'))
  },

  /**
   * Update personal conference valuew
   */
  updatePersonalConference (config) {
    return POST(this.url('personal_conferences'), config)
  },

  /**
   * Returns list of phone numbers
   */
  getPhoneNumbers (search) {
    const config = {
      params: { search: search }
    }
    return GET(this.url('contact/phone_numbers'), config)
  },

  /**
   * Returns list of contacts
   */
  getContacts (start, limit, search, isPersonal = null, filterByTags = null, filterByPersonalTags = null) {
    const config = {
      params: { start: start, limit: limit }
    }
    if (search) {
      config.params.search = search
    }
    if (isPersonal != null) {
      config.params.is_personal = isPersonal
    }
    if (filterByTags) {
      config.params.filter_by_tags = filterByTags
    }
    if (filterByPersonalTags) {
      config.params.filter_by_personal_tags = filterByPersonalTags
    }
    return GET(this.url('contacts'), config)
  },

  getContactById (id) {
    return GET(this.url(`contacts/${id}`))
  },

  /**
   * Create a new contact
   */
  createContact (contact) {
    const formData = new FormData()
    const snakeCase = string => {
      return string.replace(/\W+/g, ' ')
        .split(/ |\B(?=[A-Z])/)
        .map(word => word.toLowerCase())
        .join('_')
    }
    for (const propertyName in contact) {
      if (contact[propertyName] || (contact[propertyName] === true || contact[propertyName] === false)) {
        if (contact[propertyName] === 'null') {
          contact[propertyName] = ''
        }
        if (propertyName === 'emails') {
          formData.append(snakeCase(propertyName), JSON.stringify(contact.emails))
        } else if (propertyName === 'phoneNumbers') {
          formData.append(snakeCase(propertyName), JSON.stringify(contact.phoneNumbers))
        } else {
          formData.append(snakeCase(propertyName), contact[propertyName])
        }
      }
    }
    return POST(this.url('contacts'), formData)
  },

  /**
   * Update an existing contact
   */
  updateContact (contact) {
    const formData = new FormData()
    const snakeCase = string => {
      return string.replace(/\W+/g, ' ')
        .split(/ |\B(?=[A-Z])/)
        .map(word => word.toLowerCase())
        .join('_')
    }
    for (const propertyName in contact) {
      if (contact[propertyName] || (contact[propertyName] === true || contact[propertyName] === false)) {
        if (contact[propertyName] === 'null') {
          contact[propertyName] = ''
        }
        if (propertyName === 'emails') {
          formData.append(snakeCase(propertyName), JSON.stringify(contact.emails))
        } else if (propertyName === 'phoneNumbers') {
          formData.append(snakeCase(propertyName), JSON.stringify(contact.phoneNumbers))
        } else {
          formData.append(snakeCase(propertyName), contact[propertyName])
        }
      }
    }
    return PUT(this.url(`contacts/${contact.id}`), formData)
  },

  contactsBulkDelete (ids) {
    return POST(this.url('contact/bulk/delete'), { ids: ids })
  },

  /**
   * Import contacts
   */
  importContacts (file) {
    const formData = new FormData()
    formData.append('csv_file', file)
    return POST(this.url('contact/import'), formData)
  },

  /**
   * Export contacts
   */
  exportContacts () {
    return POST(this.url('contact/export'))
  },

  /**
   * Delete a contact
   */
  deleteContact (contact) {
    return DELETE(this.url(`contacts/${contact.id}`))
  },

  /**
   * Get contact tags
   */
  getContactTags () {
    return GET(this.url('contact/tags'))
  },

  /**
   * Create a contact tag
   */
  createContactTag (data) {
    return POST(this.url('contact/tags'), data)
  },

  /**
   * Update a contact tag
   */
  updateContactTag (data) {
    return PUT(this.url(`contact/tags/${data.id}`), data)
  },

  /**
   * Delete contact tags
   */
  deleteContactTag (data) {
    return DELETE(this.url(`contact/tags/${data.id}`))
  },

  /**
   * Get contact personal tags
   */
  getContactPersonalTags () {
    return GET(this.url('contact/personal_tags'))
  },

  /**
   * Create a contact personal tag
   */
  createContactPersonalTag (data) {
    return POST(this.url('contact/personal_tags'), data)
  },

  /**
   * Update a contact personal tag
   */
  updateContactPersonalTag (data) {
    return PUT(this.url(`contact/personal_tags/${data.id}`), data)
  },

  /**
   * Delete a contact personal tags
   */
  deleteContactPersonalTag (data) {
    return DELETE(this.url(`contact/personal_tags/${data.id}`))
  },

  getSmsToEmail () {
    return GET(this.url('sms/sms_to_email'))
  },

  setSmsToEmail (data) {
    const params = {
      sms_to_emails: data
    }
    return POST(this.url('sms/set_sms_to_email'), params)
  },

  /**
   * Returns list of chat channels
   */
  getRocketChatChannels (params, sort) {
    const limit = params?.limit || 50
    const offset = params?.offset || 0
    const sortProperty = 'lm'
    const sortDirection = sort?.sortDirection === 'asc' ? 1 : -1
    return GET(this.url(`chats/channels?limit=${limit}&offset=${offset}&sort_property=${sortProperty}&sort_direction=${sortDirection}`))
  },

  /**
   * Returns list of chat groups
   */
  getRocketChatGroups (params, sort) {
    const limit = params?.limit || 50
    const offset = params?.offset || 0
    const sortProperty = 'lm'
    const sortDirection = sort?.sortDirection === 'asc' ? 1 : -1
    return GET(this.url(`chats/groups?limit=${limit}&offset=${offset}&sort_property=${sortProperty}&sort_direction=${sortDirection}`))
  },

  /**
   * Returns list of chat direct conversations
   */
  getRocketChatDirectConversations (params, sort) {
    const limit = params?.limit || 50
    const offset = params?.offset || 0
    const sortProperty = 'lm'
    const sortDirection = sort?.sortDirection === 'asc' ? 1 : -1
    return GET(this.url(`chats/direct_messages?limit=${limit}&offset=${offset}&sort_property=${sortProperty}&sort_direction=${sortDirection}`))
  },

  /**
   * Returns list of chat SMS conversations
   */
  getRocketChatSmsConversations (params, sort) {
    const limit = params?.limit || 50
    const offset = params?.offset || 0
    const sortProperty = 'lm'
    const sortDirection = sort?.sortDirection === 'asc' ? 1 : -1
    return GET(this.url(`chats/sms_messages?limit=${limit}&offset=${offset}&sort_property=${sortProperty}&sort_direction=${sortDirection}`))
  },

  /**
   * Returns list of chat presences
   */
  listRocketChatPresences () {
    return GET(this.url('chats/presence'))
  },

  /**
   * Return user chat status
   */
  getRocketChatStatus (username) {
    return GET(this.url('chats/presence/status'), { params: { rocket_username: username } })
  },

  /**
   * Return success
   */
  setRocketChatStatus (status, message) {
    return PUT(this.url('chats/presence/update'), { status: status, message: message })
  },

  /**
   * Returns list of custom chat statuses
   */
  listRocketChatCustomStatuses () {
    return GET(this.url('chats/presence/custom'))
  },

  /**
   * Return success
   */
  createRocketChatCustomStatus (name, type) {
    return POST(this.url('chats/presence/custom/create'), { name: name, type: type })
  },

  /**
   * Return success
   */
  setRocketChatCustomStatus (customId, name, type) {
    return PUT(this.url('chats/presence/custom/update'), { customId: customId, name: name, type: type })
  },

  /**
   * Return success
   */
  deleteRocketChatCustomStatus (customId) {
    return DELETE(this.url(`chats/presence/custom/delete?customId=${customId}`))
  },

  /**
   * Return authenticated chat user
   */
  authRocketChatUser () {
    return POST(this.url('chats/auth'))
  },

  /**
   * Return created channel
   */
  newChatConversation (isSms, destinations, message, channelName, smsDestination, files, fromDid, channelType) {
    const formData = new FormData()
    // files
    let fileIndex = 0
    if (files) {
      files.forEach(file => {
        formData.append(`file_${fileIndex}`, file)
        fileIndex = fileIndex + 1
      })
    }
    // additional data
    formData.append('is_sms', isSms)
    formData.append('destinations', destinations)
    formData.append('message', message)
    formData.append('channel_name', channelName)
    formData.append('channel_type', channelType)
    formData.append('sms_destination', smsDestination)
    formData.append('from_did', fromDid)
    return POST(this.url('chats/new_conversation'), formData)
  },

  /**
   * Return success
   */
  openChannel (roomId) {
    return POST(this.url('chats/channels/open'), { room_id: roomId })
  },

  /**
   * Return success
   */
  closeChannel (roomId) {
    return POST(this.url('chats/channels/close'), { room_id: roomId })
  },

  /**
   * Return success
   */
  leaveChannel (roomId) {
    return POST(this.url('chats/channels/leave'), { room_id: roomId })
  },

  /**
   * Return success
   */
  archiveChannel (roomId) {
    return POST(this.url('chats/channels/archive'), { room_id: roomId })
  },

  /**
   * Return success
   */
  restoreChannel (roomId) {
    return POST(this.url('chats/channels/restore'), { room_id: roomId })
  },

  /**
   * Return success
   */
  joinChannel (roomId) {
    return POST(this.url('chats/channels/join'), { room_id: roomId })
  },

  /**
   * Return success
   */
  addMemberToChannel (roomId, userId) {
    return POST(this.url('chats/channels/join'), { room_id: roomId, rocket_uid: userId })
  },

  /**
   * Return success
   */
  kickFromChannel (roomId, userId) {
    return POST(this.url('chats/channels/kick'), { room_id: roomId, rocket_uid: userId })
  },

  /**
   * Return success
   */
  addChannelOwner (roomId, userId) {
    return POST(this.url('chats/channels/add_owner'), { room_id: roomId, rocket_uid: userId })
  },

  /**
   * Return success
   */
  removeChannelOwner (roomId, userId) {
    return POST(this.url('chats/channels/remove_owner'), { room_id: roomId, rocket_uid: userId })
  },

  /**
   * Return success
   */
  renameChannel (roomId, name) {
    return PUT(this.url('chats/channels/rename'), { room_id: roomId, name: name })
  },

  /**
   * Return success
   */
  channelMembers (roomId) {
    return GET(this.url(`chats/channels/members?room_id=${roomId}`))
  },

  /**
   * Return success
   */
  deleteChannel (roomId) {
    return DELETE(this.url(`chats/channels/delete?room_id=${roomId}`))
  },

  /**
   * Return success
   */
  openGroup (roomId) {
    return POST(this.url('chats/groups/open'), { room_id: roomId })
  },

  /**
   * Return success
   */
  closeGroup (roomId) {
    return POST(this.url('chats/groups/close'), { room_id: roomId })
  },

  /**
   * Return success
   */
  leaveGroup (roomId) {
    return POST(this.url('chats/groups/leave'), { room_id: roomId })
  },

  /**
   * Return success
   */
  archiveGroup (roomId) {
    return POST(this.url('chats/groups/archive'), { room_id: roomId })
  },

  /**
   * Return success
   */
  restoreGroup (roomId) {
    return POST(this.url('chats/groups/restore'), { room_id: roomId })
  },

  /**
   * Return success
   */
  addMemberToGroup (roomId, userId) {
    return POST(this.url('chats/groups/invite'), { room_id: roomId, rocket_uid: userId })
  },

  /**
   * Return success
   */
  kickFromGroup (roomId, userId) {
    return POST(this.url('chats/groups/kick'), { room_id: roomId, rocket_uid: userId })
  },

  /**
   * Return success
   */
  addGroupOwner (roomId, userId) {
    return POST(this.url('chats/groups/add_owner'), { room_id: roomId, rocket_uid: userId })
  },

  /**
   * Return success
   */
  removeGroupOwner (roomId, userId) {
    return POST(this.url('chats/groups/remove_owner'), { room_id: roomId, rocket_uid: userId })
  },

  /**
   * Return success
   */
  renameGroup (roomId, name) {
    return PUT(this.url('chats/groups/rename'), { room_id: roomId, name: name })
  },

  /**
   * Return success
   */
  groupMembers (roomId) {
    return GET(this.url(`chats/groups/members?room_id=${roomId}`))
  },

  /**
   * Return success
   */
  deleteGroup (roomId) {
    return DELETE(this.url(`chats/groups/delete?room_id=${roomId}`))
  },

  /**
   * Return current user sms DIDs
   */
  getSmsDids () {
    return GET(this.url('sms/list_dids'))
  },

  /**
   * Return success
   */
  closeDirectChat (roomId) {
    return POST(this.url('chats/direct_messages/close'), { room_id: roomId })
  },

  /**
   * Return chat users
   */
  getChatUsers () {
    return GET(this.url('users/all_chat_users'))
  },

  /**
   * Return current user
   */
  getCurrentUser () {
    return GET(this.url('users/me'))
  },

  /**
   * Return available extensions
   */
  getAvailableExtensions (extensionToCheck) {
    return GET(this.url(`users/available_extensions?extension_to_check=${extensionToCheck}`))
  },

  /**
   * Return array of video conf config
   */
  getVideoConferenceConfigs ({ start, limit, sortCol, sortDir, search } = {}) {
    const config = {
      params: {
        start: start ?? 0,
        limit: limit ?? 10,
        search: search || '',
        ...(sortCol ? { sort_column: sortCol } : {}),
        ...(sortDir ? { sort_direction: sortDir } : {})
      }
    }
    return GET(this.url('video_conference_configs'), config)
  },

  /**
   * Return array of client licenses
   */
  getLicenses () {
    return GET(this.url('clients/licenses'))
  },

  /**
   * Returns list of voicemails in Promise
   */
  getVoicemails ({ users, start, limit, sortCol, sortDir, search, filter } = {}) {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const config = {
      params: {
        start: start ?? 0,
        limit: limit ?? 10,
        folder: filter?.folder || 'all',
        category: filter?.category || null,
        date: filter?.date || '',
        time_zone: timeZone,
        ...(sortCol ? { sort_column: sortCol } : {}),
        ...(sortDir ? { sort_direction: sortDir } : {}),
        ...(search ? { search: search } : {}),
        ...(users ? { filter_by_extension: users } : [])
      }
    }
    return GET(this.url('voicemails'), config)
  },

  getVoicemailById (voicemailId) {
    return GET(this.url(`voicemails/${voicemailId}`))
  },

  getVoicemailCustomFolders () {
    return GET(this.url('voicemails/list_custom_folders'))
  },

  getVoicemailCategories () {
    return GET(this.url('voicemails/list_categories'))
  },

  markVmFavorite (voicemailId) {
    return PUT(this.url(`voicemails/${voicemailId}/mark_as_favorite`))
  },

  unmarkVmFavorite (voicemailId) {
    return PUT(this.url(`voicemails/${voicemailId}/unmark_as_favorite`))
  },

  bulkFavorite (voicemailIds) {
    return POST(this.url('voicemails/bulk/mark_as_favorite'), { ids: voicemailIds })
  },

  bulkUnfavorite (voicemailIds) {
    return POST(this.url('voicemails/bulk/unmark_as_favorite'), { ids: voicemailIds })
  },

  moveToCustomFolder (voicemailId, customFolder) {
    return PUT(this.url(`voicemails/${voicemailId}/move_to_custom_folder`), { custom_folder: customFolder })
  },

  removeFromCustomFolder (voicemailId) {
    return PUT(this.url(`voicemails/${voicemailId}/remove_from_custom_folder`))
  },

  bulkMoveToCustomFolder (voicemailIds, customFolder) {
    return POST(this.url('voicemails/bulk/move_to_custom_folder'), { ids: voicemailIds, custom_folder: customFolder })
  },

  bulkRemoveFromCustomFolder (voicemailIds) {
    return POST(this.url('voicemails/bulk/remove_from_custom_folder'), { ids: voicemailIds })
  },

  /**
   * Returns list of greeting messages
   */
  getVMByFolder (users, folder) {
    const config = {
      params: {
        display_all: true,
        search: folder,
        ...(users ? { filter_by_extension: users } : [])
      }
    }
    return GET(this.url('voicemails'), config)
  },

  getVmbPinRestriction () {
    return GET(this.url('voicemail_boxes/password_restrictions'))
  },

  /**
   * Archive voicemail message
   */
  archiveVMail (id, name) {
    return PUT(this.url(`voicemails/${id}/archive`), { name: name })
  },

  /**
   * Restore from archive and replace folder
   */
  restoreVMArchive (id, folder) {
    return PUT(this.url(`voicemails/${id}/restore`), { folder: folder })
  },

  /**
   * Set user emergency caller id profile and location
   */
  setEmergencyCID (profileID, locationID) {
    return PUT(this.url('users/emergency_caller_id'), {
      emergency_callerid_callback_profile_id: profileID,
      emergency_callerid_dispatchable_location_id: locationID
    })
  },

  /**
   * Retrieve list of users for which CDRs can be watched.
   */
  getCDRUsers () {
    return GET(this.url('cdrs/can_watch_users'))
  },

  /**
   * Retrieve  CDRs for currect user.
   */
  getCDRs (start, limit, dateFrom, dateTo, user) {
    const config = {
      params: {
        sort_column: 'calldate',
        sort_direction: 'DESC',
        start: start,
        limit: limit,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
        ...(user ? { filter_by_extension: user } : {})
      }
    }
    return GET(this.url('cdrs/friendly_calls_by_date'), config)
  },

  /**
   * Retrieve an specific CDR info.
   */
  getCDR (id) {
    return GET(this.url(`cdrs/${id}`))
  },

  /**
   * Retrieve CDRs as a CSV
   */
  getCDRsCSV (dateFrom, dateTo, user) {
    const config = {
      params: {
        sort_column: 'calldate',
        sort_direction: 'DESC',
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
        ...(user ? { filter_by_extension: user } : {})
      }
    }
    return GET(this.url('cdrs/friendly_calls_by_date_export'), config)
  },

  /**
   * Get CDR recording
   */
  getCDRRec (id) {
    return GET(this.url(`cdrs/${id}/recording`), { responseType: 'blob' })
  },

  /**
   * Get bulk CDR recordings
   */
  getBulkCDRRec (encodedIds) {
    return GET(this.url(`/archive/cdrs/recording/bulk_download/${encodedIds}`))
  },

  /**
   * Get feature codes configured on clusterpbx
   */
  getFeatureCodes () {
    const config = {
      params: { start: 0, limit: 50 }
    }
    return GET(this.url('feature_codes'), config)
  },

  /**
   * Add buddy card to dashboard.
   */
  addBuddy (exten, sort) {
    return POST(this.url('panel_buddies'), { username: exten, sort: sort })
  },

  /**
   * Remove buddy card from dashboard
   */
  removeBuddy (exten) {
    return DELETE(this.url(`panel_buddies/${exten}`))
  },

  /**
   * Update buddy card position on dashboard
   */
  updateBuddy (exten, sort) {
    return PUT(this.url(`panel_buddies/${exten}`), { username: exten, sort: sort })
  },

  /**
   * Update callpanel user username, email, password etc
   */
  updateUser (ID, config) {
    return PUT(this.url(`users/${ID}`), config)
  },

  /**
   * Toggle queue card watch on dashboard
   */
  queueWatchToggle (queue) {
    return PATCH(this.url('queues/watch'), { name: queue.id })
  },

  /**
   * Get queue member pause causes.
   */
  getMemberPauseCodes (param = {}) {
    const config = {
      params: {
        start: param.start || 0,
        limit: param.limit || 12,
        sort_column: param.sortBy || 'description',
        sort_direction: param.sortDir || 'ASC',
        ...(param.search ? { search: param.search } : {})
      }
    }
    return GET(this.url('queue_member_pause_codes'), config)
  },

  /**
   * Retrieve voicemail box setting by mailbox
   */
  getVMBSettings (mailbox) {
    return GET(this.url(`voicemail_boxes/${mailbox}`))
  },

  /**
   * Returns list of voicemail boxes the user can browse.
   * If no vm boxes configured then Response.data array is empty.
   */
  getVMBoxesToWatch () {
    return GET(this.url('voicemail_boxes/can_watch_users'))
  },

  /**
   * Voicemail greeting files: name, busy, unavail, temp
   */
  VMGreetingGet (name, vmbid) {
    return GET(this.url(`voicemail_boxes/${vmbid}/${name}`),
      { responseType: 'blob' })
  },

  VMGreetingSet (name, vmbid, data) {
    return PUT(this.url(`voicemail_boxes/${vmbid}/${name}`), data)
  },

  VMGreetingTTSGet (name, vmbid) {
    return GET(this.url(`voicemail_boxes/${vmbid}/${name}/tts`))
  },

  VMGreetingTTSSet (name, vmbid, data) {
    return PUT(this.url(`voicemail_boxes/${vmbid}/${name}/tts`), data)
  },

  VMGreetingDel (name, vmbid) {
    return DELETE(this.url(`voicemail_boxes/${vmbid}/${name}`))
  },

  toggleVMailRead (id, status) {
    const url = status
      ? this.url(`voicemails/${id}/mark_as_read`)
      : this.url(`voicemails/${id}/mark_as_new`)
    return PUT(url)
  },

  removeVMailMsg (id) {
    return DELETE(this.url(`voicemails/${id}`))
  },

  /**
   * Transfer voicemail message
   */
  VMXferTo (vmid, toExten) {
    return POST(this.url(`voicemails/${vmid}/transfer_voicemail`),
      { extensions: [toExten] })
  },

  /**
   * Voicemail messge download
   */
  VMAudioSrc (id) {
    return GET(this.url(`voicemails/${id}/download?format=mp3`), { responseType: 'blob' })
  },

  /**
   * Voicemail bulk mark as read
   */
  VMBulkMarkRead (ids) {
    return POST(this.url('voicemails/bulk/mark_as_read'), { ids })
  },

  /**
   * Voicemail bulk mark as new
   */
  VMBulkMarkNew (ids) {
    return POST(this.url('voicemails/bulk/mark_as_new'), { ids })
  },

  /**
   * Voicemail bulk transfer
   */
  VMBulkTransfer (ids, extensions) {
    return POST(this.url('voicemails/bulk/transfer_voicemail'), { ids, extensions })
  },

  /**
   * Voicemail bulk delete
   */
  VMBulkDelete (ids) {
    return POST(this.url('voicemails/bulk/delete'), { ids })
  },

  /**
   * Save voicemail settings to API.
   */
  saveVMSettings (mailbox, settings) {
    return PUT(this.url(`voicemail_boxes/${mailbox}`), settings)
  },

  /**
   * Settings update
   */
  getSettingsCanWatchUsers () {
    const get = opt => GET(this.url(`users/${opt}/can_watch_users`))
    return Promise.allSettled([
      get('do_not_disturb'),
      get('call_waiting'),
      get('intercom'),
      get('emergency_caller_id'),
      get('forward'),
      get('follow_me'),
      get('click_to_dial'),
      get('dids'),
      get('screen_pop')
    ])
  },

  /**
   * get all user settings by user ID
   */
  getUserSettings (id, settingKey) {
    return GET(this.url(`users/${id}/${settingKey}`))
  },

  getUserPreferences (id) {
    return GET(this.url(`users/${id}/preferences`))
  },

  settingsDND (userID, enabled) {
    return PUT(this.url(`users/${userID}/do_not_disturb`),
      { enabled: enabled ? '1' : '0' })
  },

  settingsCallWaiting (userID, enabled) {
    return PUT(this.url(`users/${userID}/call_waiting`),
      { enabled: enabled ? '1' : '0' })
  },

  settingsIntercom (userID, { deskphone, callpanel, anywhere }) {
    const data = {
      deskPhoneEnabled: deskphone ? '1' : '0',
      callPanelEnabled: callpanel ? '1' : '0',
      clearlyAnywhereEnabled: anywhere ? '1' : '0'
    }
    return PUT(this.url(`users/${userID}/intercom`), data)
  },

  settingsCallFwd (userID, enabled, number) {
    if (enabled) {
      return PUT(this.url(`users/${userID}/forward`), { to: number })
    }
    return DELETE(this.url(`users/${userID}/forward`))
  },

  settingsFollowMe (userID, data) {
    return PUT(this.url(`users/${userID}/follow_me`), data)
  },

  getRingStrategies () {
    return GET(this.url('ring_strategies'))
  },

  settingsClickToDial (userID, data) {
    return PUT(this.url(`users/${userID}/click_to_dial`), data)
  },

  settingsScreenPop (userID, data) {
    return PUT(this.url(`users/${userID}/screen_pop`), data)
  },

  settingsShowMissedCalls (userID, data) {
    return PUT(this.url(`users/${userID}/preferences`), {
      show_missed_calls: data ? '1' : '0'
    })
  },

  getBlacklist () {
    return GET(this.url('blacklists'))
  },

  saveBlacklist (data) {
    const params = {
      name: data.name,
      number: data.number
    }
    return POST(this.url('blacklists'), params)
  },

  deleteBlacklist (id) {
    return DELETE(this.url(`blacklists/${id}`))
  },

  saveAllBlacklist (data) {
    const params = { blacklist_numbers: data }
    return POST(this.url('blacklists/bulk_save'), params)
  },

  setClearlyAnywherelandingPage (userID, data) {
    return PUT(this.url(`users/${userID}/preferences`), {
      clearly_anywhere_landing_page: data
    })
  },

  getToggleDestinations () {
    return GET(this.url('toggle_destinations'))
  },

  switchToggleDest (id, dest) {
    return PUT(this.url(`toggle_destinations/${id}`), { active_opt: dest })
  },

  /**
   * Toggle queue card watch on dashboard
   */
  watchConference (id, enable) {
    return PATCH(this.url(`conferences/${id}/watch`), { watch: enable })
  },

  /**
   * Emergency Caller ID API
   */
  getECIDProfiles () {
    const config = {
      params: {
        sort_column: 'name',
        limit: 100
      }
    }
    return GET(this.url('emergency_caller_id_callback_profiles'), config)
  },

  getECIDLocations () {
    const config = {
      params: {
        sort_column: 'name',
        limit: 100
      }
    }
    return GET(this.url('emergency_caller_id_dispatchable_locations'), config)
  },

  /**
   * Get notification settings
   */
  getNotificationSettings () {
    return GET(this.url('panel_notifications'))
  },

  /**
   * Set notification settings
   */
  setNotificationSettings (notifications) {
    return POST(this.url('panel_notifications'), notifications)
  },

  /**
   * Get OTP settings for callpanel user
   */
  getOTPConfig () {
    return GET(this.url('otp_configuration'))
  },

  /**
   * Update callpanel user OTP settings
   */
  updateOTP (setting) {
    return PUT(this.url('otp_configuration'), setting)
  },

  /**
   * Get ClearlyAnywhere account data
   */
  getClearlyAnywhereAcc () {
    return GET(this.url('clearly_anywhere/qr_code_link'))
  },

  /**
   * Get ClearlyAnywhere more info link state
   */
  getClearlyAnywhereMoreInfoLinkState () {
    return GET(this.url('clearly_anywhere/more_info_link_enabled'))
  },

  /**
   * Send Clearly Anywhere setup email with the QR code
   */
  postClearlyAnywhereCreds () {
    return POST(this.url('mailer/setup_clearly_anywhere_email'))
  },

  /**
   * get announcement page
   */
  getAnnouncements (start, limit, search) {
    const config = {
      params: {
        start: start,
        limit: limit,
        search: search
      }
    }
    return GET(this.url('announcements'), config)
  },

  /**
   * get announcement binary by ID
   */
  getAnnouncementStream (id) {
    return GET(this.url(`announcements/${id}/stream`),
      { responseType: 'blob' })
  },

  /**
   * get list of supported TTS voices
   */
  getTTSVoiceTypes () {
    return GET(this.url('announcement/list_speech_voices'))
  },

  /**
   * get time conditions for followme
   */
  getFmeTimeConditions () {
    return GET(this.url('users/follow_me/time_conditions'))
  },

  /**
   * get preview TTS announcement binary
   */
  previewTTSAnnouncementStream (data) {
    return POST(this.url('announcement/preview_text_to_speech'), data, { responseType: 'blob' })
  },

  /**
   * Update announcement data and TTS if exists
   */
  updateAnnouncement (id, data) {
    return PUT(this.url(`announcements/${id}`), data)
  },

  /**
   * upload new announcement
   */
  uploadAnnouncement (id, localObjURL) {
    return fetch(localObjURL).then(resp => {
      return resp.blob()
    }).then(blob => {
      const form = new FormData()
      form.append('recording', blob)
      return PUT(this.url(`announcements/${id}/upload`), form)
    })
  },

  /** Reset Clearly Anywhere QR code (auth token)
   */
  resetClearlyAnywhereQrCode () {
    return POST(this.url('clearly_anywhere/regenerate_auth_token'))
  },

  /**
   * Get list of pictures data
   */
  userPictureProfilesMetadata () {
    return GET(this.url('user_picture_profiles/metadata'))
  },

  /**
   * Upload a new picture profile
   */
  userPictureProfilesUpload (file) {
    const formData = new FormData()
    formData.append('file', file)
    return POST(this.url('user_picture_profiles'), formData)
  },

  /**
   * Switch your from active picture profile: support gmail or uploaded
   */
  switchUserPictureProfile (pictureType) {
    return POST(this.url('user_picture_profiles/switch_picture_type'), { picture_type: pictureType })
  },

  /**
   * Delete a picture profile by ID
   */
  userPictureProfilesDelete (id) {
    return DELETE(this.url(`user_picture_profiles/${id}`))
  },

  /**
   * Get available favicon sizes
   */
  getFaviconSizes () {
    return GET(this.url('branding/favicon_sizes'))
  },

  /**
   * Get branding configuration
   */
  getBranding () {
    return GET(this.url('branding'))
  },

  /**
   * Get queue summary report
   */
  getQueueSummaryReport (queues, startDate, endDate, timeZone = 'UTC', hourly = false) {
    const config = {
      params: {
        queues: queues,
        start_date: startDate,
        end_date: endDate,
        timezone: timeZone
      }
    }
    if (hourly) config.params.hourly = true
    return GET(this.url('queues/reports/summary'), config)
  },

  /**
   * Get queue call details report
   */
  getQueueCallDetailsReport (queues, startDate, endDate, timeZone = 'UTC', limit = 100, offset = 0) {
    const config = {
      params: {
        queues: queues,
        start_date: startDate,
        end_date: endDate,
        timezone: timeZone,
        limit: limit,
        offset: offset
      }
    }
    return GET(this.url('queues/reports/call_details'), config)
  },

  /**
   * Get queue event logs by call
   */
  getQueueEventLogsByCall (callId, timeZone = 'UTC') {
    const config = {
      params: {
        timezone: timeZone
      }
    }
    return GET(this.url(`queues/reports/logs/${callId}`), config)
  },

  /**
   * Get queue agent details by agent
   */
  getQueueAgentDetails (agentExtension, startDate, endDate, timeZone = 'UTC', limit = 100, offset = 0) {
    const config = {
      params: {
        start_date: startDate,
        end_date: endDate,
        timezone: timeZone,
        limit: limit,
        offset: offset
      }
    }
    return GET(this.url(`queues/reports/logs/agent/${agentExtension}`), config)
  }
}
